import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Button, Icon, Typography } from '@popmenu/common-ui';
import { Mail } from '@popmenu/web-icons';

import { createEvent } from '~/utils/eventable';
import { withIntl } from '../../utils/withIntl';
import { withStyles } from '../../utils/withStyles';
import signInUserMutation from '../../libs/gql/mutations/users/signInUserMutation.gql';
import signInStyles from '../../assets/jss/shared/signInStyles';

import BasicForm, { SubmitGroup, TextFieldGroup } from '../../admin/shared/forms/BasicForm';
import SocialControls from './SocialControls';
import { AH, AHLevelProvider } from '../../consumer/shared/AccessibleHeading';

const SignInForm = ({
  'aria-label': ariaLabel,
  afterSignIn = null,
  classes,
  email = null,
  facebookOauthDisabled,
  onlyForm = false,
  openForgotModal = null,
  restaurantId = null,
  style = null,
  t,
  variant = 'standard',
}) => {
  const renderLinks = () => (
    <div className={classes.signInLinks}>
      {openForgotModal && (
        <Button
          color="inherit"
          data-cy="sign_in_forgot_password"
          onClick={() => {
            createEvent({
              eventableType: 'BecomeVipV2',
              eventType: 'sign_in_forgot_password',
            });
            openForgotModal();
          }}
          variant="text"
        >
          <FormattedMessage defaultMessage="Forgot password?" />
        </Button>
      )}
    </div>
  );

  const renderForm = () => (
    <BasicForm
      aria-labelledby="sign-in-title"
      defaultValues={{
        email,
      }}
      id="sign-in-form"
      labelPosition="top"
      mutate={{
        mutation: signInUserMutation,
        onCompleted: () => {
          if (typeof afterSignIn === 'function') {
            afterSignIn();
          }
        },
        toVariables: variables => ({
          ...variables,
          restaurantId,
        }),
      }}
      onSubmit={({ facebookCode, googleCode }) => {
        if (facebookCode) {
          createEvent({
            eventableType: 'BecomeVipV2',
            eventType: 'sign_in_modal_facebook',
          });
        } else if (googleCode) {
          createEvent({
            eventableType: 'BecomeVipV2',
            eventType: 'sign_in_modal_google',
          });
        } else {
          createEvent({
            eventableType: 'BecomeVipV2',
            eventType: 'sign_in_modal_button',
          });
        }
      }}
    >
      {({ submitManual }) => (
        <React.Fragment>
          <TextFieldGroup
            field="email"
            placeholder={t('users.email')}
            style={style}
            type="email"
            variant={variant}
          />
          <TextFieldGroup
            field="password"
            placeholder={t('users.password')}
            style={style}
            type="password"
            variant={variant}
            data-tour-id="sign-in-form-password"
          />

          <SubmitGroup block color="primary" icon={false} justifyContent="center" size="lg" title="Sign In" />

          <div className={classes.socialSeparatorContainer}>
            <div className={classes.socialSeparator} aria-hidden="true">
              <div className={classes.socialSeparatorLine} />
            </div>
            <Typography align="center" className={classes.socialSeparatorText}>
              <FormattedMessage defaultMessage="or" />
            </Typography>
          </div>

          <div className={classes.signInLinks}>
            <div>
              <Button
                role="link"
                aria-label={ariaLabel}
                className={classes.signInLinkButton}
                onClick={() => {
                  createEvent({
                    eventableType: 'BecomeVipV2',
                    eventType: 'sign_in_magic_link_button',
                  });
                  openForgotModal();
                }}
                size="large"
                textTransform="none"
                variant="outlined"
              >
                <Icon icon={Mail} className={classes.buttonIcon} />
                <FormattedMessage defaultMessage=" Email me a Magic Link" />
              </Button>
            </div>

            <SocialControls
              facebookOauthDisabled={facebookOauthDisabled}
              submit={(e, variables) => submitManual(variables)}
            />
          </div>
        </React.Fragment>
      )}
    </BasicForm>
  );

  return (
    (onlyForm ? (
      <React.Fragment>
        {renderForm()}
        {renderLinks()}

      </React.Fragment>
    ) : (
      <React.Fragment>
        <AH typography align="center" className={classes.signInTitle} id="sign-in-title" variant="h3">
          <FormattedMessage defaultMessage="Sign In" />
        </AH>
        <AHLevelProvider>
          {renderForm()}
          {renderLinks()}
        </AHLevelProvider>
      </React.Fragment>
    ))
  );
};

SignInForm.propTypes = {
  afterSignIn: PropTypes.func,
  email: PropTypes.string,
  facebookOauthDisabled: PropTypes.bool.isRequired,
  onlyForm: PropTypes.bool,
  openForgotModal: PropTypes.func,
  restaurantId: PropTypes.number,
  style: PropTypes.object,
  t: PropTypes.func.isRequired,
  variant: PropTypes.string,
};

export default withStyles(signInStyles)(withIntl(SignInForm));
